import { AsurionLogoDark } from "@expert/common-ui/assets/AsurionLogoDark";
import { useIsPacingActive } from "@expert/sales-tools";
import { Flex, ScrollArea, Stack, UnstyledButton } from "@mantine/core";
import { useEffect, useMemo, useRef, useState } from "react";

import { useAnalytics } from "@/analytics";
import { env } from "@/config";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { useAgentFriendlyName, useLogger } from "@/sdk";

import { AutoAvailablePill } from "./auto-available/AutoAvailablePill";
import { useAutoAvailableCountdown } from "./auto-available/useAutoAvailableCountdown";
import { LeaderboardSummaryCard } from "./cards/LeaderboardSummaryCard";
import { usePayMetrics } from "./data/usePayMetrics";
import { isDataverseCard } from "./data/utils";
import { EMPTY_HUB_METRICS } from "./emptyHubMetrics";
import styles from "./expertHub.module.css";
import { type GreetingTier, Greeting } from "./greeting/Greeting";
import { type MetricsTab, MyMetricsHeader } from "./metrics/MyMetricsHeader";
import { isLiveCardVisible, TodayMetrics } from "./metrics/TodayMetrics";
import { YesterdayMetrics } from "./metrics/YesterdayMetrics";
import { useHubPanelStore } from "./right-panel/useHubPanelStore";

function resolveGreetingTier(cardCount: number, showLeaderboard: boolean): GreetingTier {
    if (cardCount === 0 && !showLeaderboard) return "empty";
    if (cardCount >= 3) return "rich";
    return "sparse";
}

const EXPERT_HUB_SHOWN_SESSION_KEY_PREFIX = "expertHub:shown";

export function ExpertHub() {
    const firstName = useAgentFriendlyName();
    const { payMetrics } = usePayMetrics();
    const { dispatcher } = useAnalytics();
    const logger = useLogger({ module: "ExpertHub" });
    const { hubMetrics, leaderboards } = usePartnerConfig();
    const isPacingActive = useIsPacingActive();
    const togglePanel = useHubPanelStore((s) => s.togglePanel);
    const [activeTab, setActiveTab] = useState<MetricsTab>("recent");
    const hasDispatchedShown = useRef(false);
    const isVoice = env.channel !== "chat";
    const {
        active: autoAvailableActive,
        phase: autoAvailablePhase,
        countdownMs: autoAvailableCountdownMs,
        onExpire: onAutoAvailableExpire,
    } = useAutoAvailableCountdown();

    const cards = hubMetrics ?? EMPTY_HUB_METRICS;

    const showLeaderboard = isVoice && leaderboards.length > 0;

    const visibleCards = useMemo(
        () => cards.filter((card) => isDataverseCard(card) || isLiveCardVisible(card, isPacingActive)),
        [cards, isPacingActive],
    );

    const greetingTier = resolveGreetingTier(visibleCards.length, showLeaderboard);

    const availableTabs = useMemo<MetricsTab[]>(() => {
        const tabs: MetricsTab[] = [];
        if (visibleCards.some(isDataverseCard)) tabs.push("recent");
        if (visibleCards.some((card) => card.source.kind === "gamification")) tabs.push("realtime");
        return tabs;
    }, [visibleCards]);

    const hasMetrics = availableTabs.length > 0;
    const activeMetricsTab = availableTabs.includes(activeTab) ? activeTab : (availableTabs[0] ?? activeTab);

    useEffect(() => {
        if (hasDispatchedShown.current) return;
        hasDispatchedShown.current = true;
        const key = `${EXPERT_HUB_SHOWN_SESSION_KEY_PREFIX}:${env.channel}`;
        if (globalThis.sessionStorage.getItem(key)) return;
        globalThis.sessionStorage.setItem(key, "1");
        logger.info("Off-call hub: dispatching shown event");
        void dispatcher.dispatchBusinessEvent("ExpertHubShown", { channel: env.channel });
    }, [dispatcher, logger]);

    return (
        <Flex className={styles.root} role="region" aria-label="Off-call hub">
            <ScrollArea classNames={{ root: styles.scrollRoot, viewport: styles.scrollViewport }} type="auto">
                <Stack className={styles.content} gap="xl">
                    <Greeting name={firstName} tier={greetingTier} />

                    {showLeaderboard && (
                        <LeaderboardSummaryCard
                            leaderboards={leaderboards}
                            onClick={() => {
                                if (useHubPanelStore.getState().view !== "leaderboard") {
                                    void dispatcher.dispatchBusinessEvent("ExpertHubLeaderboardOpened", {
                                        channel: env.channel,
                                    });
                                }
                                togglePanel("leaderboard");
                            }}
                        />
                    )}

                    {hasMetrics && (
                        <Stack gap="md">
                            <MyMetricsHeader
                                value={activeMetricsTab}
                                onChange={setActiveTab}
                                tabs={availableTabs}
                                modifiedOn={payMetrics?.modifiedOn ?? null}
                            />
                            {activeMetricsTab === "recent" ? <YesterdayMetrics /> : <TodayMetrics />}
                        </Stack>
                    )}
                </Stack>
            </ScrollArea>

            <UnstyledButton
                className={styles.assistButton}
                onClick={() => togglePanel("assist")}
                aria-label="Ask Assist"
            >
                <AsurionLogoDark />
            </UnstyledButton>

            <AutoAvailablePill
                active={autoAvailableActive}
                phase={autoAvailablePhase}
                countdownMs={autoAvailableCountdownMs}
                onExpire={onAutoAvailableExpire}
                isVoice={isVoice}
            />
        </Flex>
    );
}
